<template>
  <div>
    <div>
      <!-- ===== STEP 1: Plan Questions (general individual only) ===== -->
      <div class="license-questions-card bg-white border border-border rounded-md lg:mb-5 mb-4"
        v-if="state.opt_type == 'general' && state.enroll_option == 'individual'">

        <!-- Q1: Who are you buying a plan for? -->
        <div class="flex w-full !m-0 p-4 gap-[8px] lg:items-center flex-col lg:flex-row items-start border-b border-border">
          <div class="flex xl:items-center gap-[8px] w-full items-baseline">
            <span class="number-badge shrink-0">1</span>
            <h2 class="card-title !m-0">Who are you buying a plan for?</h2>
          </div>
          <fieldset class="flex flex-wrap items-center gap-[16px] ms-[27px] lg:ms-0 shrink-0">
            <legend class="sr-only">Plan purchase for</legend>
            <div class="field-radio-block">
              <label class="radio radio-outline-primary field-radio !mb-0">
                <input type="radio" :checked="state.whoFor === 'Your student'"
                  @change="onWhoForChange('Your student')" name="whoFor" value="Your student" />
                <span>Your student</span><span class="checkmark"></span>
              </label>
            </div>
            <div class="field-radio-block">
              <label class="radio radio-outline-primary field-radio !mb-0">
                <input type="radio" :checked="state.whoFor === 'Yourself'"
                  @change="onWhoForChange('Yourself')" name="whoFor" value="Yourself" />
                <span>Yourself</span><span class="checkmark"></span>
              </label>
            </div>
            <div class="field-radio-block">
              <label class="radio radio-outline-primary field-radio !mb-0">
                <input type="radio" :checked="state.whoFor === 'You and your family'"
                  @change="onWhoForChange('You and your family')" name="whoFor" value="You and your family" />
                <span>You and your family</span><span class="checkmark"></span>
              </label>
            </div>
          </fieldset>
        </div>

        <!-- Q2: CDL (hidden when buying for your student) -->
        <div v-if="state.whoFor && state.whoFor !== 'Your student'"
          class="flex w-full !m-0 p-4 gap-[8px] lg:items-center flex-col lg:flex-row items-start border-b border-border">
          <div class="flex xl:items-center gap-[8px] w-full items-baseline">
            <span class="number-badge shrink-0">2</span>
            <div class="cdl-question-heading flex items-center flex-wrap gap-2">
              <h2 class="card-title !m-0">Do You Have A Commercial Driver's License (CDL)?</h2>
              <div class="info-wrapper relative inline-flex group z-[60]">
                <span class="info-icon" tabindex="0" aria-label="What is a CDL?">i</span>
                <div class="tooltip-box hidden group-hover:block group-focus-within:block" role="tooltip">
                  <div class="tooltip-title">What is a Commercial Driver's License (CDL)?</div>
                  <div class="tooltip-body">
                    You have a CDL if you are licensed to drive large or specialized vehicles like
                    tractor-trailers, buses, or vehicles carrying hazardous materials.<br><br>
                    If your license is a standard passenger license for personal vehicles
                    (cars, SUVs, pickup trucks), you do not have a CDL.
                  </div>
                </div>
              </div>
            </div>
          </div>
          <fieldset class="flex items-center gap-[16px] ms-[27px] lg:ms-0 shrink-0">
            <legend class="sr-only">Commercial Driver's License (CDL)</legend>
            <div class="field-radio-block">
              <label class="radio radio-outline-primary field-radio !mb-0">
                <input type="radio" :checked="state.hasCDL === 'yes'"
                  @change="onHasCDLChange('yes')" name="hasCDL" value="yes" />
                <span>Yes</span><span class="checkmark"></span>
              </label>
            </div>
            <div class="field-radio-block">
              <label class="radio radio-outline-primary field-radio !mb-0">
                <input type="radio" :checked="state.hasCDL === 'no'"
                  @change="onHasCDLChange('no')" name="hasCDL" value="no" />
                <span>No</span><span class="checkmark"></span>
              </label>
            </div>
          </fieldset>
        </div>

        <!-- Q3: Plan interest (Yourself / Family after CDL answer) -->
        <div v-if="showPlanInterest"
          class="flex w-full !m-0 p-4 gap-[8px] lg:items-center flex-col lg:flex-row items-start border-b border-border">
          <div class="flex xl:items-center gap-[8px] w-full items-baseline">
            <span class="number-badge shrink-0">{{ planInterestQuestionNumber }}</span>
            <h2 class="card-title !m-0">Which plans are you interested in?</h2>
          </div>
          <fieldset class="flex flex-wrap items-center gap-[16px] ms-[27px] lg:ms-0 shrink-0">
            <legend class="sr-only">Plan interest</legend>
            <div v-for="option in planInterestOptions" :key="option" class="field-radio-block">
              <label class="radio radio-outline-primary field-radio !mb-0">
                <input type="radio" :checked="state.planInterest === option"
                  @change="onPlanInterestChange(option)" name="planInterest" :value="option" />
                <span>{{ option }}</span><span class="checkmark"></span>
              </label>
            </div>
          </fieldset>
        </div>

      </div>

      <!-- SEIU / Affiliated union section (unchanged) -->
      <div class="bg-white border border-border rounded-md lg:mb-5 mb-4"
        v-if="(state.enroll_option == 'affilliated' && state.opt_type == 'general' || state.opt_type == 'SEIU721')">
        <div class="flex w-full border-b border-border !m-0 p-4 gap-[8px] items-center">
          <span class="number-badge">1</span>
          <h2 class="card-title !m-0">Union Member Information</h2>
        </div>
        <div class="p-4">
          <div class="form-group mb-4 flex items-center justify-start gap-[16px]">
            <span id="isSEIU-label" class="font-normal text-mute text-sm mb-0 inline-block">
              Are you a SEIU721 Union Member?<span class="text-error">*</span>
            </span>
            <fieldset class="flex items-center gap-[20px]" role="radiogroup" aria-labelledby="isSEIU-label">
              <legend class="sr-only">SEIU721 Union Member</legend>
              <div class="field-radio-block">
                <label class="radio radio-outline-primary field-radio !mb-0">
                  <input type="radio" :checked="state.isSEIU === 'yes'"
                    @change="$emit('updateState', { key: 'isSEIU', value: 'yes' })" name="isSEIU"
                    value="yes" /><span>Yes</span><span class="checkmark"></span>
                </label>
              </div>
              <div class="field-radio-block">
                <label class="radio radio-outline-primary field-radio !mb-0">
                  <input type="radio" name="isSEIU" @change="$emit('updateState', { key: 'isSEIU', value: 'no' })"
                    value="no" /><span>No</span><span class="checkmark"></span>
                </label>
              </div>
            </fieldset>
          </div>
          <div v-if="state.isSEIU === 'no'" class="bg-white border border-border rounded-md lg:mb-5 mb-4">
            <div class="text-center p-4 md:w-[400px] w-auto mx-auto">
              <span
                class="w-[60px] h-[60px] rounded-full flex items-center justify-center border bg-[#fff2f2] border-border mx-auto mb-4">
                <img src="@/assets/images/warning-info.svg" alt="info" />
              </span>
              <h3 class="font-bold text-blackPrimary text-lg">We're Sorry</h3>
              <p class="content !m-0">
                To confirm Union Membership or to become a Member of the Union, please contact a SEIU721 Membership
                Analyst at membership@SEIU721.org or call (213) 251-3757. You may also click the following link to
                become a member or verify membership <u class="text-blue-500"><a
                    href="https://www.seiu721.org/create-account.php"
                    target="_blank">https://www.seiu721.org/create-account.php</a></u>
              </p>
            </div>
          </div>
          <div class="form-group" v-if="state.isSEIU == 'yes' && state.isFAM == 'yes'">
            <label class="font-normal text-mute text-sm mb-2 inline-block">
              Select Company<span class="text-error">*</span>
            </label>
            <select id="company" name="company" v-model="selectedCompany" @change="updateSelectedCompany"
              class="form-mask">
              <option v-for="company in state.companies" :key="company.id" :value="company">
                {{ company.company_name }}
              </option>
            </select>
            <small class="mt-[8px] block text-mute text-xs font-normal">
              If you do not see your employer in this drop-down list, please contact a SEIU721 Membership Analyst at
              membership@SEIU721.org or call (213) 251-3757 to confirm if you are able to enroll.
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  shouldShowPlanInterest,
  getPlanInterestQuestionNumber,
  getPlanInterestOptions,
  canProceedFromQuestions,
  getEnrollmentParameter,
} from "@/utils/enrollmentFlow";

export default {
  props: {
    state: Object,
  },
  data() {
    return {
      selectedCompany: null,
      showNextError: false,
    };
  },
  computed: {
    showPlanInterest() {
      return shouldShowPlanInterest(this.state);
    },

    planInterestQuestionNumber() {
      return getPlanInterestQuestionNumber(this.state);
    },

    planInterestOptions() {
      return getPlanInterestOptions(this.state);
    },

    canProceed() {
      return canProceedFromQuestions(this.state);
    },
  },
  methods: {
    updateSelectedCompany() {
      this.$emit('updateState', { key: 'company', value: this.selectedCompany });
      this.$emit('updateState', { key: 'company_number', value: this.selectedCompany.company });
    },

    syncEnrollmentParameter(overrides = {}) {
      const nextState = { ...this.state, ...overrides };
      this.$emit('updateState', {
        key: 'enrollment_parameter',
        value: getEnrollmentParameter(nextState),
      });
    },

    onWhoForChange(value) {
      this.$emit('updateState', { key: 'whoFor', value });
      this.$emit('updateState', { key: 'planInterest', value: '' });
      if (value === 'Your student') {
        this.$emit('updateState', { key: 'hasCDL', value: '' });
      }
      this.syncEnrollmentParameter({ whoFor: value, planInterest: '', hasCDL: value === 'Your student' ? '' : this.state.hasCDL });
      this.$emit('updateState', { key: 'disp_enroll_section', value: false });
      this.showNextError = false;
    },

    onHasCDLChange(value) {
      this.$emit('updateState', { key: 'hasCDL', value });
      this.$emit('updateState', { key: 'planInterest', value: '' });
      this.syncEnrollmentParameter({ hasCDL: value, planInterest: '' });
      this.$emit('updateState', { key: 'disp_enroll_section', value: false });
      this.showNextError = false;
    },

    onPlanInterestChange(value) {
      this.$emit('updateState', { key: 'planInterest', value });
      this.syncEnrollmentParameter({ planInterest: value });
      this.showNextError = false;
    },

    goToStep2() {
      if (!this.canProceed) {
        this.showNextError = true;
        return;
      }
      this.showNextError = false;
      this.$emit('updateState', { key: 'plan_questions_complete', value: true });
    },
  }
};
</script>

<style scoped>
.info-icon {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 18px;
  height: 18px;
  border: 1.5px solid #b23f22;
  border-radius: 50%;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  color: #b23f22;
  line-height: 1;
  text-transform: lowercase;
}

.info-wrapper {
  vertical-align: middle;
}

.info-wrapper:hover,
.info-wrapper:focus-within {
  z-index: 100;
}

.cdl-question-heading {
  overflow: visible;
}

.tooltip-box {
  position: absolute;
  bottom: calc(100% + 8px);
  top: auto;
  left: 0;
  width: min(300px, calc(100vw - 48px));
  background: #ffffff;
  border: 1.5px solid #1a1a1a;
  border-radius: 6px;
  z-index: 100;
  color: #333;
  box-shadow: 0 4px 10px rgba(0,0,0,0.12);
  overflow: hidden;
  isolation: isolate;
}

.tooltip-title {
  font-size: 15px;
  line-height: 20px;
  font-weight: 600;
  color: #1a1a1a;
  padding: 12px;
  background-color: #d9f9f9;
  border-radius: 4px 4px 0 0;
}

.tooltip-body {
  font-size: 13px;
  line-height: 18px;
  color: #1a1a1a;
  padding: 12px;
  font-weight: 500;
  background-color: #ffffff;
  border-radius: 0 0 4px 4px;
}

@media (min-width: 992px) {
  .tooltip-box {
    bottom: auto;
    top: 50%;
    left: calc(100% + 10px);
    transform: translateY(-50%);
    width: 300px;
  }
}

.license-questions-card {
  overflow: visible;
}

.license-questions-card > div {
  overflow: visible;
}

.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}
</style>
